import { FC } from 'react'

import { Text } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import { ProjectManagementProjectEmployeesAssignmentEnum } from 'generated/resources/datatable.graphql'
import {
  BulkAssignProjectWorkerDocument,
  BulkChangeAssignmentDocument,
  ProjectManagementProjectsDataCollectionModalsAssignEmployeesDocument,
  ProjectsDataCollectionDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'
import { LoadingState } from 'modules/project_management/components/LoadingState'
import Assign from 'modules/project_management/components/modals/AssignEmployeesModal'

interface AssignEmployeesModalProps {
  ids: string[]
  onCloseModal?: () => void
  initialEmployeesAssignment?: ProjectManagementProjectEmployeesAssignmentEnum | undefined
}

export const AssignEmployeesModal: FC<AssignEmployeesModalProps> = ({
  ids,
  onCloseModal,
  initialEmployeesAssignment,
}) => {
  const { company } = useCurrentSession()

  const multipleSelection = ids.length > 1

  const totalSelectedProjectWorkers = ids.length

  const [bulkAssign, { loading: loadingBulkAssign }] = useMutation(
    BulkAssignProjectWorkerDocument,
    {
      extractorFn: (data) =>
        data.projectManagementMutations.bulkAssignProjectmanagementProjectWorker,
      refetchQueries: [ProjectsDataCollectionDocument],
      awaitRefetchQueries: true,
    }
  )
  const [bulkChangeAssignment, { loading: loadingBulkChangeAssignment }] = useMutation(
    BulkChangeAssignmentDocument,
    {
      extractorFn: (data) =>
        data.projectManagementMutations.bulkChangeAssignmentProjectmanagementProject,
      refetchQueries: [ProjectsDataCollectionDocument],
      awaitRefetchQueries: true,
    }
  )

  const { loading: loadingQuery, data } = useQuery(
    ProjectManagementProjectsDataCollectionModalsAssignEmployeesDocument,
    {
      variables: {
        projectId: ids[0] ?? '0',
        companyId: company.id,
        featureIds: [Features.projects_overview],
      },
      skip: multipleSelection || initialEmployeesAssignment === 'company',
    }
  )

  if (loadingQuery) return <LoadingState />

  const hasNotificationsOnAssignation =
    !!data?.companies.featureAvailabilitiesConnection.nodes.find(
      (feature) => feature.id === Features.projects_overview
    )?.available

  const getInitialAssignedEmployeeIds = (): string[] => {
    if (multipleSelection || initialEmployeesAssignment === 'company') {
      return []
    }

    // used flatMap instead of map for type safety; in order to avoid undefined if no pw.employee
    // (that should always come as it is asked, but typescript does not know)
    const assignedEmployeeIds =
      data?.projectManagement.project?.projectWorkers.flatMap((projectWorker) => {
        return projectWorker.employee ? [projectWorker.employee.id] : []
      }) || []

    return assignedEmployeeIds
  }

  const onClose = () => {
    onCloseModal?.()
  }

  const onSubmit = async (
    _initialEmployeesAssignment: ProjectManagementProjectEmployeesAssignmentEnum | undefined,
    newEmployeesAssignment: ProjectManagementProjectEmployeesAssignmentEnum,
    employeeSelection: EmployeeSelectorV2MultiSelectState
  ) => {
    const assignmentChanged =
      multipleSelection || _initialEmployeesAssignment !== newEmployeesAssignment
        ? await bulkChangeAssignment({
            variables: {
              ids,
              employeesAssignment: newEmployeesAssignment,
            },
          })
        : undefined

    const employeeIds = employeeSelection.type === 'manual' ? employeeSelection.ids : []
    const peopleAssigned =
      newEmployeesAssignment === 'manual'
        ? await bulkAssign({
            variables: {
              employeeIds,
              projectIds: ids,
            },
          })
        : undefined

    let handled = undefined

    if (assignmentChanged) {
      handled = handleResult(assignmentChanged, () => undefined)
    } else if (peopleAssigned) {
      handled = handleResult(peopleAssigned, () => undefined)
    }

    if (handled) {
      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('project_management.toasts.assign_employees_multiple_projects_success'),
        })

        track(trackingEvents.projectManagement.bulkActions.employeeProjectsAssignment.eventSubmit, {
          projects_employee_assignment_selection: newEmployeesAssignment,
          projects_assigned_count: ids.length,
          notify_employees: hasNotificationsOnAssignation,
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_saving'),
        })
      }
    }

    onClose()
  }

  return (
    <Assign
      iconName={'add-user'}
      title={i18n.t('project_management.modals.assign_employees.title')}
      description={i18n.tView('project_management.modals.assign_employees.groupDescription', {
        n_projects: <Text size="300">{ids.length}</Text>,
      })}
      loading={loadingBulkAssign || loadingBulkChangeAssignment}
      showWarning={multipleSelection && !!totalSelectedProjectWorkers}
      submitButton={i18n.t('project_management.modals.assign_employees.submit')}
      employeesAssignment={initialEmployeesAssignment}
      initialAssignedEmployeeIds={getInitialAssignedEmployeeIds()}
      onClose={onClose}
      onSubmit={onSubmit}
    />
  )
}
